import React, { useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import RotatedBookIcon from "./RotatedBookIcon";
import {
  Download,
  X,
  Bold,
  Italic,
  Underline,
  Zap,
  Palette,
  Link,
  Unlink,
  GitCommitHorizontal,
  Settings2,
} from "lucide-react";
import storageService from "../services/storageService";

const EnhancementEditor = ({ image, capturedStyles, onClose }) => {
  // State initialization with defaults (will be updated by useEffect)
  const [backgroundType, setBackgroundType] = useState("gradient3");
  const [padding, setPadding] = useState({
    top: 100,
    right: 100,
    bottom: 100,
    left: 100,
    uniform: true,
  });
  const [borderRadius, setBorderRadius] = useState(50);
  const [textElements, setTextElements] = useState([]);
  const [selectedTextId, setSelectedTextId] = useState(null);
  const [logo, setLogo] = useState(null);
  const [logoPosition, setLogoPosition] = useState({ x: 50, y: 50 }); // Center position by default
  const [logoSizePercentage, setLogoSizePercentage] = useState(10);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedTextId, setDraggedTextId] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isLogoDragging, setIsLogoDragging] = useState(false);
  const [isLogoResizing, setIsLogoResizing] = useState(false);
  const [logoSelected, setLogoSelected] = useState(false);
  const [resizeStartState, setResizeStartState] = useState(null);
  const [cursorOverHandle, setCursorOverHandle] = useState(null);
  const [resizeCorner, setResizeCorner] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [customColor, setCustomColor] = useState("#ff6b6b");
  const [savedTextSettings, setSavedTextSettings] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const canvasRef = useRef(null);
  const mobileCanvasRef = useRef(null);
  const logoInputRef = useRef(null);

  // Create BibeCheck logo preserving original transparency and curved edges
  const createBibeCheckLogoImage = useCallback((size) => {
    return new Promise((resolve) => {
      // Load the actual logo image
      const logoImg = new Image();
      logoImg.onload = () => {
        // Create canvas to process the original image
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = size;
        canvas.height = size;

        // Enable high-quality rendering
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Clear the canvas to ensure transparency (this should make it transparent, not white)
        ctx.clearRect(0, 0, size, size);

        // Calculate dimensions to maintain aspect ratio
        const logoSize = size * 0.85;
        const logoX = (size - logoSize) / 2;
        const logoY = (size - logoSize) / 2;

        // Draw the original PNG image which should preserve its transparency
        ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);

        // Convert to image with transparency preserved
        const img = new Image();
        img.onload = () => resolve(img);
        img.src = canvas.toDataURL('image/png');
      };

      logoImg.onerror = () => {
        // Fallback: create a simple placeholder if image fails to load
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = size;
        canvas.height = size;

        // Draw simple blue circle as fallback
        ctx.fillStyle = '#4F8FF7';
        ctx.beginPath();
        ctx.arc(size/2, size/2, size/3, 0, 2 * Math.PI);
        ctx.fill();

        const img = new Image();
        img.onload = () => resolve(img);
        img.src = canvas.toDataURL('image/png');
      };

      // Load the high-quality logo with proper transparency
      logoImg.crossOrigin = 'anonymous'; // Allow cross-origin loading
      logoImg.src = '/bibecheck-logo-big.png';
    });
  }, []);

  const backgroundOptions = [
    { id: "none", label: "None", style: "transparent" },
    // Original vibrant gradients
    {
      id: "gradient1",
      label: "Electric",
      style: "linear-gradient(135deg, #ff0080 0%, #ff8c00 100%)",
    },
    {
      id: "gradient2",
      label: "Neon",
      style: "linear-gradient(135deg, #00ff88 0%, #00ccff 100%)",
    },
    {
      id: "gradient3",
      label: "Plasma",
      style: "linear-gradient(135deg, #8a2be2 0%, #ff1493 100%)",
    },
    {
      id: "gradient4",
      label: "Solar",
      style: "linear-gradient(135deg, #ff4500 0%, #ffd700 100%)",
    },
    {
      id: "gradient5",
      label: "Miami",
      style: "linear-gradient(135deg, #ff006e 0%, #8338ec 100%)",
    },
    {
      id: "gradient6",
      label: "Cyber",
      style: "linear-gradient(135deg, #00ffff 0%, #ff00ff 100%)",
    },
    {
      id: "gradient7",
      label: "Laser",
      style: "linear-gradient(135deg, #ff073a 0%, #c147e9 100%)",
    },
    {
      id: "gradient8",
      label: "Atomic",
      style: "linear-gradient(135deg, #39ff14 0%, #ff073a 100%)",
    },
    {
      id: "gradient9",
      label: "Matrix",
      style: "linear-gradient(135deg, #0068f0 0%, #16213e 50%, #1a1a2e 100%)",
    },
    {
      id: "gradient10",
      label: "Fire",
      style: "linear-gradient(135deg, #ff9500 0%, #ff0000 100%)",
    },
    {
      id: "gradient11",
      label: "Ice",
      style: "linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)",
    },
    {
      id: "gradient12",
      label: "Sunset",
      style: "linear-gradient(135deg, #ff7675 0%, #74b9ff 100%)",
    },
    // New beautiful gradients
    {
      id: "gradient13",
      label: "Aurora",
      style: "linear-gradient(135deg, #00c9ff 0%, #92fe9d 100%)",
    },
    {
      id: "gradient14",
      label: "Royal",
      style: "linear-gradient(135deg, #0068f0 0%, #764ba2 100%)",
    },
    {
      id: "gradient15",
      label: "Peachy",
      style: "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)",
    },
    {
      id: "gradient16",
      label: "Ocean",
      style: "linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)",
    },
    {
      id: "gradient17",
      label: "Forest",
      style: "linear-gradient(135deg, #134e5e 0%, #71b280 100%)",
    },
    {
      id: "gradient18",
      label: "Cherry",
      style: "linear-gradient(135deg, #eb3349 0%, #f45c43 100%)",
    },
    {
      id: "gradient19",
      label: "Cosmic",
      style: "linear-gradient(135deg, #4776e6 0%, #8e54e9 100%)",
    },
    {
      id: "gradient20",
      label: "Golden",
      style: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    },
    {
      id: "gradient21",
      label: "Velvet",
      style: "linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)",
    },
    {
      id: "gradient22",
      label: "Aqua",
      style: "linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)",
    },
    {
      id: "gradient23",
      label: "Mango",
      style: "linear-gradient(135deg, #ffe259 0%, #ffa751 100%)",
    },
    {
      id: "gradient24",
      label: "Emerald",
      style: "linear-gradient(135deg, #56ab2f 0%, #a8e6cf 100%)",
    },
    // Solid colors
    { id: "plain1", label: "White", style: "#ffffff" },
    { id: "plain2", label: "Vibrant Blue", style: "#0068f0" },
    { id: "plain3", label: "Dark", style: "#343a40" },
    { id: "plain4", label: "Black", style: "#000000" },
    // Custom color option (at the end)
    { id: "custom", label: "Custom", style: customColor },
  ];

  const fontOptions = [
    {
      id: "inter",
      name: "Inter",
      family: "Inter, system-ui, -apple-system, sans-serif",
    },
    { id: "roboto", name: "Roboto", family: "Roboto, Arial, sans-serif" },
    {
      id: "playfair",
      name: "Playfair Display",
      family: '"Playfair Display", Georgia, serif',
    },
    {
      id: "opensans",
      name: "Open Sans",
      family: '"Open Sans", Helvetica, sans-serif',
    },
    { id: "lato", name: "Lato", family: "Lato, Verdana, sans-serif" },
    { id: "poppins", name: "Poppins", family: "Poppins, Tahoma, sans-serif" },
  ];

  // Helper function to get the active canvas based on viewport
  const getActiveCanvas = useCallback(() => {
    const isMobile = window.innerWidth < 1024; // lg breakpoint
    return isMobile ? mobileCanvasRef.current : canvasRef.current;
  }, []);

  const generatePreview = useCallback(
    (forDownload = false) => {
      const canvas = getActiveCanvas();
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      const img = new Image();

      img.onload = () => {
        const totalPadding = {
          horizontal: padding.left + padding.right,
          vertical: padding.top + padding.bottom,
        };

        canvas.width = img.width + totalPadding.horizontal;
        canvas.height = img.height + totalPadding.vertical;

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (backgroundType !== "none") {
          const bgOption = backgroundOptions.find(
            (bg) => bg.id === backgroundType
          );
          if (bgOption) {
            if (bgOption.style.includes("gradient")) {
              const gradient = ctx.createLinearGradient(
                0,
                0,
                canvas.width,
                canvas.height
              );
              if (backgroundType === "gradient1") {
                gradient.addColorStop(0, "#ff0080");
                gradient.addColorStop(1, "#ff8c00");
              } else if (backgroundType === "gradient2") {
                gradient.addColorStop(0, "#00ff88");
                gradient.addColorStop(1, "#00ccff");
              } else if (backgroundType === "gradient3") {
                gradient.addColorStop(0, "#8a2be2");
                gradient.addColorStop(1, "#ff1493");
              } else if (backgroundType === "gradient4") {
                gradient.addColorStop(0, "#ff4500");
                gradient.addColorStop(1, "#ffd700");
              } else if (backgroundType === "gradient5") {
                gradient.addColorStop(0, "#ff006e");
                gradient.addColorStop(1, "#8338ec");
              } else if (backgroundType === "gradient6") {
                gradient.addColorStop(0, "#00ffff");
                gradient.addColorStop(1, "#ff00ff");
              } else if (backgroundType === "gradient7") {
                gradient.addColorStop(0, "#ff073a");
                gradient.addColorStop(1, "#c147e9");
              } else if (backgroundType === "gradient8") {
                gradient.addColorStop(0, "#39ff14");
                gradient.addColorStop(1, "#ff073a");
              } else if (backgroundType === "gradient9") {
                gradient.addColorStop(0, "#0068f0");
                gradient.addColorStop(0.5, "#16213e");
                gradient.addColorStop(1, "#1a1a2e");
              } else if (backgroundType === "gradient10") {
                gradient.addColorStop(0, "#ff9500");
                gradient.addColorStop(1, "#ff0000");
              } else if (backgroundType === "gradient11") {
                gradient.addColorStop(0, "#74b9ff");
                gradient.addColorStop(1, "#0984e3");
              } else if (backgroundType === "gradient12") {
                gradient.addColorStop(0, "#ff7675");
                gradient.addColorStop(1, "#74b9ff");
              } else if (backgroundType === "gradient13") {
                gradient.addColorStop(0, "#00c9ff");
                gradient.addColorStop(1, "#92fe9d");
              } else if (backgroundType === "gradient14") {
                gradient.addColorStop(0, "#0068f0");
                gradient.addColorStop(1, "#764ba2");
              } else if (backgroundType === "gradient15") {
                gradient.addColorStop(0, "#ff9a9e");
                gradient.addColorStop(1, "#fecfef");
              } else if (backgroundType === "gradient16") {
                gradient.addColorStop(0, "#2193b0");
                gradient.addColorStop(1, "#6dd5ed");
              } else if (backgroundType === "gradient17") {
                gradient.addColorStop(0, "#134e5e");
                gradient.addColorStop(1, "#71b280");
              } else if (backgroundType === "gradient18") {
                gradient.addColorStop(0, "#eb3349");
                gradient.addColorStop(1, "#f45c43");
              } else if (backgroundType === "gradient19") {
                gradient.addColorStop(0, "#4776e6");
                gradient.addColorStop(1, "#8e54e9");
              } else if (backgroundType === "gradient20") {
                gradient.addColorStop(0, "#f093fb");
                gradient.addColorStop(1, "#f5576c");
              } else if (backgroundType === "gradient21") {
                gradient.addColorStop(0, "#d299c2");
                gradient.addColorStop(1, "#fef9d7");
              } else if (backgroundType === "gradient22") {
                gradient.addColorStop(0, "#89f7fe");
                gradient.addColorStop(1, "#66a6ff");
              } else if (backgroundType === "gradient23") {
                gradient.addColorStop(0, "#ffe259");
                gradient.addColorStop(1, "#ffa751");
              } else if (backgroundType === "gradient24") {
                gradient.addColorStop(0, "#56ab2f");
                gradient.addColorStop(1, "#a8e6cf");
              }
              ctx.fillStyle = gradient;
            } else {
              ctx.fillStyle = bgOption.style;
            }
            ctx.fillRect(0, 0, canvas.width, canvas.height);
          }
        }

        // Draw image with rounded corners if specified
        if (borderRadius > 0) {
          ctx.save();
          ctx.beginPath();
          const x = padding.left;
          const y = padding.top;
          const width = img.width;
          const height = img.height;
          const radius = Math.min(borderRadius, width / 2, height / 2);

          ctx.moveTo(x + radius, y);
          ctx.lineTo(x + width - radius, y);
          ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
          ctx.lineTo(x + width, y + height - radius);
          ctx.quadraticCurveTo(
            x + width,
            y + height,
            x + width - radius,
            y + height
          );
          ctx.lineTo(x + radius, y + height);
          ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
          ctx.lineTo(x, y + radius);
          ctx.quadraticCurveTo(x, y, x + radius, y);
          ctx.closePath();
          ctx.clip();

          ctx.drawImage(img, padding.left, padding.top);
          ctx.restore();
        } else {
          ctx.drawImage(img, padding.left, padding.top);
        }

        textElements.forEach((textEl) => {
          const fontWeight = textEl.bold ? "bold" : "normal";
          const fontStyle = textEl.italic ? "italic" : "normal";
          ctx.font = `${fontStyle} ${fontWeight} ${textEl.fontSize}px ${textEl.fontFamily}`;
          ctx.fillStyle = textEl.color;
          ctx.textAlign = "left";
          ctx.textBaseline = "top";

          if (textEl.shadow) {
            ctx.shadowColor = "rgba(0, 0, 0, 0.5)";
            ctx.shadowBlur = 4;
            ctx.shadowOffsetX = 2;
            ctx.shadowOffsetY = 2;
          }

          ctx.fillText(textEl.text, textEl.x, textEl.y);

          // Draw underline if enabled
          if (textEl.underline) {
            const textMetrics = ctx.measureText(textEl.text);
            const underlineY = textEl.y + textEl.fontSize + 2;
            ctx.strokeStyle = textEl.color;
            ctx.lineWidth = Math.max(1, textEl.fontSize / 20);
            ctx.beginPath();
            ctx.moveTo(textEl.x, underlineY);
            ctx.lineTo(textEl.x + textMetrics.width, underlineY);
            ctx.stroke();
          }

          if (textEl.shadow) {
            ctx.shadowColor = "transparent";
            ctx.shadowBlur = 0;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 0;
          }
        });

        // Always render a logo - either custom or BibeCheck logo
        if (logo) {
          const logoImg = new Image();
          logoImg.onload = () => {
            // Calculate logo size relative to IMAGE width (not canvas width)
            const imageWidth = canvas.width - padding.left - padding.right;
            const actualLogoSize = Math.round(
              imageWidth * (logoSizePercentage / 100)
            );

            // Calculate aspect ratio and maintain proportions
            const aspectRatio = logoImg.naturalWidth / logoImg.naturalHeight;
            let logoWidth, logoHeight;

            if (aspectRatio > 1) {
              // Wider than tall
              logoWidth = actualLogoSize;
              logoHeight = actualLogoSize / aspectRatio;
            } else {
              // Taller than wide or square
              logoHeight = actualLogoSize;
              logoWidth = actualLogoSize * aspectRatio;
            }

            // Position logo: percentage-based positioning with canvas center as reference
            let logoX, logoY;
            if (logoPosition.x <= 100 && logoPosition.y <= 100) {
              // Center the logo by default (x:50, y:50 = exact center)
              logoX = (canvas.width - logoWidth) / 2 + ((logoPosition.x - 50) * canvas.width / 100);
              logoY = (canvas.height - logoHeight) / 2 + ((logoPosition.y - 50) * canvas.height / 100);
            } else {
              // Legacy pixel positioning from right-bottom
              logoX = canvas.width - logoWidth - logoPosition.x;
              logoY = canvas.height - logoHeight - logoPosition.y;
            }
            ctx.drawImage(logoImg, logoX, logoY, logoWidth, logoHeight);

            // ONLY draw selection outline and resize handles if NOT for download AND logo is selected
            if (!forDownload && logoSelected) {
              ctx.strokeStyle = "#3b82f6";
              ctx.lineWidth = 2;
              ctx.strokeRect(
                logoX - 2,
                logoY - 2,
                logoWidth + 4,
                logoHeight + 4
              );

              // Draw resize handles on all corners
              ctx.fillStyle = "#3b82f6";
              const handleSize = 8;
              const handles = [
                { x: logoX, y: logoY }, // Top-left
                { x: logoX + logoWidth, y: logoY }, // Top-right
                { x: logoX, y: logoY + logoHeight }, // Bottom-left
                { x: logoX + logoWidth, y: logoY + logoHeight }, // Bottom-right
              ];

              handles.forEach((handle) => {
                ctx.fillRect(
                  handle.x - handleSize / 2,
                  handle.y - handleSize / 2,
                  handleSize,
                  handleSize
                );
              });
            }
          };
          logoImg.src = logo;
        } else {
          // Render BibeCheck logo when no custom logo is uploaded
          const logoSize = 160; // Fixed size for BibeCheck logo (4x bigger: 40 * 4)
          const margin = 20; // Distance from bottom-right corner

          // Position at lower right corner
          const logoX = canvas.width - logoSize - margin;
          const logoY = canvas.height - logoSize - margin;

          // Use our RotatedBookIcon component
          createBibeCheckLogoImage(logoSize).then((logoImg) => {
            ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
          });
        }
      };

      img.src = image;
    },
    [
      image,
      backgroundType,
      padding,
      textElements,
      logo,
      logoPosition,
      logoSizePercentage,
      logoSelected,
      backgroundOptions,
      borderRadius,
    ]
  );

  // Load saved settings on mount
  useEffect(() => {
    const loadSavedSettings = async () => {
      try {
        const settings = await storageService.getEnhancementSettings();

        // Apply saved settings
        setBackgroundType(settings.backgroundType);
        setCustomColor(settings.customColor);
        setPadding(settings.padding);
        setBorderRadius(settings.borderRadius);
        setLogoPosition(settings.logoPosition || { x: 50, y: 50 });
        setLogoSizePercentage(settings.logoSizePercentage);
        setSavedTextSettings(settings.lastTextSettings);

        // Create text element from saved data if it exists
        if (
          settings.lastTextSettings.lastTextValue &&
          settings.lastTextSettings.lastTextX !== null &&
          settings.lastTextSettings.lastTextY !== null
        ) {
          const savedTextElement = {
            id: Date.now(),
            text: settings.lastTextSettings.lastTextValue,
            x: settings.lastTextSettings.lastTextX,
            y: settings.lastTextSettings.lastTextY,
            fontSize: settings.lastTextSettings.fontSize,
            fontFamily: settings.lastTextSettings.fontFamily,
            color: settings.lastTextSettings.color,
            bold: settings.lastTextSettings.bold,
            italic: settings.lastTextSettings.italic,
            underline: settings.lastTextSettings.underline,
            shadow: settings.lastTextSettings.shadow,
          };
          setTextElements([savedTextElement]);
          setSelectedTextId(savedTextElement.id);
        }

        // Generate preview with loaded settings
        setTimeout(() => generatePreview(), 100);
      } catch (error) {
        console.error("Failed to load saved settings:", error);
      }
    };

    loadSavedSettings();
  }, []); // Only run on mount

  // Save settings whenever they change
  useEffect(() => {
    const saveSettings = async () => {
      try {
        await storageService.saveEnhancementSettings({
          backgroundType,
          customColor,
          padding,
          borderRadius,
          logoPosition,
          logoSizePercentage,
        });
      } catch (error) {
        console.error("Failed to save settings:", error);
      }
    };

    // Debounce saving
    const timeoutId = setTimeout(saveSettings, 500);
    return () => clearTimeout(timeoutId);
  }, [
    backgroundType,
    customColor,
    padding,
    borderRadius,
    logoPosition,
    logoSizePercentage,
  ]);

  // Save text settings whenever textElements change
  useEffect(() => {
    const saveTextSettings = async () => {
      // Find the most recently edited or selected text element
      let textToSave = null;

      if (selectedTextId) {
        textToSave = textElements.find((el) => el.id === selectedTextId);
      } else if (textElements.length > 0) {
        // If no selected text, save the last text element
        textToSave = textElements[textElements.length - 1];
      }

      if (textToSave) {
        try {
          await storageService.updateEnhancementTextSettings({
            fontSize: textToSave.fontSize,
            fontFamily: textToSave.fontFamily,
            color: textToSave.color,
            bold: textToSave.bold,
            italic: textToSave.italic,
            underline: textToSave.underline,
            shadow: textToSave.shadow,
            lastTextValue: textToSave.text,
            lastTextX: textToSave.x,
            lastTextY: textToSave.y,
          });
        } catch (error) {
          console.error("Failed to save text settings:", error);
        }
      }
    };

    // Debounce the save operation
    const timeoutId = setTimeout(saveTextSettings, 500);
    return () => clearTimeout(timeoutId);
  }, [textElements, selectedTextId]);

  // Save on component unmount
  useEffect(() => {
    return () => {
      // Save the last text value and position when component unmounts
      if (textElements.length > 0) {
        const lastText = textElements[textElements.length - 1];
        if (lastText && lastText.text) {
          storageService
            .updateEnhancementTextSettings({
              lastTextValue: lastText.text,
              lastTextX: lastText.x,
              lastTextY: lastText.y,
            })
            .catch((error) => {
              console.error("Failed to save text value on unmount:", error);
            });
        }
      }
    };
  }, [textElements]);

  // Handle smart transitions between uniform and independent padding
  const handlePaddingToggle = () => {
    setPadding((prev) => {
      if (prev.uniform) {
        // Unlocking: Copy current uniform value to all sides
        return {
          top: prev.top,
          right: prev.top,
          bottom: prev.top,
          left: prev.top,
          uniform: false,
          horizontalLocked: false,
          verticalLocked: false,
        };
      } else {
        // Locking: Use the maximum value for uniform padding
        const maxValue = Math.max(prev.top, prev.right, prev.bottom, prev.left);
        return {
          top: maxValue,
          right: maxValue,
          bottom: maxValue,
          left: maxValue,
          uniform: true,
          horizontalLocked: false,
          verticalLocked: false,
        };
      }
    });
  };

  // Handle horizontal lock toggle (left/right together)
  const handleHorizontalLockToggle = () => {
    setPadding((prev) => {
      if (prev.horizontalLocked) {
        return { ...prev, horizontalLocked: false };
      } else {
        const avgHorizontal = Math.round((prev.left + prev.right) / 2);
        return {
          ...prev,
          left: avgHorizontal,
          right: avgHorizontal,
          horizontalLocked: true,
        };
      }
    });
  };

  // Handle vertical lock toggle (top/bottom together)
  const handleVerticalLockToggle = () => {
    setPadding((prev) => {
      if (prev.verticalLocked) {
        return { ...prev, verticalLocked: false };
      } else {
        const avgVertical = Math.round((prev.top + prev.bottom) / 2);
        return {
          ...prev,
          top: avgVertical,
          bottom: avgVertical,
          verticalLocked: true,
        };
      }
    });
  };

  // Add keyboard shortcut for deleting selected logo
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Delete key to remove selected custom logo
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (logoSelected && logo) { // Only works for custom uploaded logos
          e.preventDefault();
          setShowDeleteConfirm(true);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [logoSelected, logo]);

  // Handle window resize and mobile/desktop layout changes
  useEffect(() => {
    const handleResize = () => {
      // Small delay to ensure layout has settled
      setTimeout(() => {
        if (getActiveCanvas()) {
          generatePreview();
        }
      }, 150);
    };

    // Initial call to ensure canvas is drawn on mount
    setTimeout(() => {
      if (getActiveCanvas()) {
        generatePreview();
      }
    }, 200);

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [generatePreview]);

  // Specifically handle mobile view activation
  useEffect(() => {
    const checkMobileLayout = () => {
      const isMobile = window.innerWidth < 1024; // lg breakpoint
      setTimeout(() => {
        if (getActiveCanvas()) {
          generatePreview();
        }
      }, 250);
    };

    // Check on mount and when layout might change
    checkMobileLayout();

    const mediaQuery = window.matchMedia('(max-width: 1023px)');
    const handleMediaChange = () => {
      setTimeout(() => {
        if (getActiveCanvas()) {
          generatePreview();
        }
      }, 300);
    };

    mediaQuery.addEventListener('change', handleMediaChange);
    return () => mediaQuery.removeEventListener('change', handleMediaChange);
  }, [generatePreview, getActiveCanvas]);

  useEffect(() => {
    // Load Google Fonts and preload for canvas rendering
    const loadGoogleFonts = () => {
      // Add Google Fonts link if not already present
      if (!document.querySelector('link[href*="fonts.googleapis.com"]')) {
        const link = document.createElement("link");
        link.href =
          "https://fonts.googleapis.com/css2?family=Inter:wght@400;700&family=Roboto:wght@400;700&family=Open+Sans:wght@400;700&family=Lato:wght@400;700&family=Poppins:wght@400;700&family=Playfair+Display:wght@400;700&display=swap";
        link.rel = "stylesheet";
        document.head.appendChild(link);
      }
    };

    const preloadFonts = async () => {
      loadGoogleFonts();

      // Wait a bit for Google Fonts to load
      setTimeout(() => {
        const fontPromises = fontOptions.map((font) => {
          return document.fonts.load(`16px ${font.family}`);
        });

        Promise.all(fontPromises)
          .then(() => generatePreview())
          .catch((error) => {
            // console.log("Some fonts failed to load:", error);
            generatePreview(); // Generate anyway with fallback fonts
          });
      }, 500);
    };

    preloadFonts();
  }, [generatePreview]);

  const addTextElement = (initialText = "") => {
    const canvas = canvasRef.current;
    let centerX = 150;
    let topCenterY = 50; // Position at top
    let defaultFontSize = 50;

    if (canvas) {
      centerX = canvas.width / 2;
      topCenterY = padding.top + 20; // Position closer to top
      // Use saved font size or default
      defaultFontSize =
        savedTextSettings?.fontSize ||
        Math.max(100, Math.round(canvas.width * 0.08));

      // Ensure text doesn't start outside canvas bounds
      centerX = Math.max(0, Math.min(canvas.width - 200, centerX)); // Reserve 200px for text width
      topCenterY = Math.max(
        0,
        Math.min(canvas.height - defaultFontSize, topCenterY)
      );
    }

    // Use saved position if available and valid
    const savedX = savedTextSettings?.lastTextX;
    const savedY = savedTextSettings?.lastTextY;
    const hasValidSavedPosition =
      savedX !== null &&
      savedX !== undefined &&
      savedY !== null &&
      savedY !== undefined;

    // Use saved text settings or defaults
    const newTextElement = {
      id: Date.now(),
      text: initialText || savedTextSettings?.lastTextValue || "",
      x: hasValidSavedPosition ? savedX : centerX,
      y: hasValidSavedPosition ? savedY : topCenterY,
      fontSize: savedTextSettings?.fontSize || defaultFontSize,
      fontFamily:
        savedTextSettings?.fontFamily ||
        "Inter, system-ui, -apple-system, sans-serif",
      color: savedTextSettings?.color || "#000000",
      bold: savedTextSettings?.bold || false,
      italic: savedTextSettings?.italic || false,
      underline: savedTextSettings?.underline || false,
      shadow: savedTextSettings?.shadow || false,
    };
    setTextElements([...textElements, newTextElement]);
    setSelectedTextId(newTextElement.id);
    return newTextElement;
  };

  const updateTextElement = (id, updates) => {
    setTextElements((prev) =>
      prev.map((el) => {
        if (el.id !== id) return el;

        const updatedElement = { ...el, ...updates };

        // Apply bounds checking if position or font size changed
        if (
          updates.x !== undefined ||
          updates.y !== undefined ||
          updates.fontSize !== undefined ||
          updates.text !== undefined
        ) {
          const canvas = canvasRef.current;
          if (canvas) {
            // Create a temporary canvas context to measure text dimensions
            const tempCanvas = document.createElement("canvas");
            const tempCtx = tempCanvas.getContext("2d");
            const fontWeight = updatedElement.bold ? "bold" : "normal";
            const fontStyle = updatedElement.italic ? "italic" : "normal";
            tempCtx.font = `${fontStyle} ${fontWeight} ${updatedElement.fontSize}px ${updatedElement.fontFamily}`;

            const textMetrics = tempCtx.measureText(updatedElement.text);
            const textWidth = textMetrics.width;
            const textHeight = updatedElement.fontSize;

            // Constrain position within canvas bounds
            updatedElement.x = Math.max(
              0,
              Math.min(canvas.width - textWidth, updatedElement.x)
            );
            updatedElement.y = Math.max(
              0,
              Math.min(canvas.height - textHeight, updatedElement.y)
            );
          }
        }

        return updatedElement;
      })
    );
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => setLogo(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const generateCleanCanvasForDownload = () => {
    const canvas = getActiveCanvas();
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const img = new Image();

    return new Promise((resolve) => {
      img.onload = () => {
        const totalPadding = {
          horizontal: padding.left + padding.right,
          vertical: padding.top + padding.bottom,
        };

        canvas.width = img.width + totalPadding.horizontal;
        canvas.height = img.height + totalPadding.vertical;

        // COMPLETELY clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Draw background
        if (backgroundType !== "none") {
          const bgOption = backgroundOptions.find(
            (bg) => bg.id === backgroundType
          );
          if (bgOption) {
            if (bgOption.style.includes("gradient")) {
              const gradient = ctx.createLinearGradient(
                0,
                0,
                canvas.width,
                canvas.height
              );
              if (backgroundType === "gradient1") {
                gradient.addColorStop(0, "#ff0080");
                gradient.addColorStop(1, "#ff8c00");
              } else if (backgroundType === "gradient2") {
                gradient.addColorStop(0, "#00ff88");
                gradient.addColorStop(1, "#00ccff");
              } else if (backgroundType === "gradient3") {
                gradient.addColorStop(0, "#8a2be2");
                gradient.addColorStop(1, "#ff1493");
              } else if (backgroundType === "gradient4") {
                gradient.addColorStop(0, "#ff4500");
                gradient.addColorStop(1, "#ffd700");
              } else if (backgroundType === "gradient5") {
                gradient.addColorStop(0, "#ff006e");
                gradient.addColorStop(1, "#8338ec");
              } else if (backgroundType === "gradient6") {
                gradient.addColorStop(0, "#00ffff");
                gradient.addColorStop(1, "#ff00ff");
              } else if (backgroundType === "gradient7") {
                gradient.addColorStop(0, "#ff073a");
                gradient.addColorStop(1, "#c147e9");
              } else if (backgroundType === "gradient8") {
                gradient.addColorStop(0, "#39ff14");
                gradient.addColorStop(1, "#ff073a");
              } else if (backgroundType === "gradient9") {
                gradient.addColorStop(0, "#0068f0");
                gradient.addColorStop(0.5, "#16213e");
                gradient.addColorStop(1, "#1a1a2e");
              } else if (backgroundType === "gradient10") {
                gradient.addColorStop(0, "#ff9500");
                gradient.addColorStop(1, "#ff0000");
              } else if (backgroundType === "gradient11") {
                gradient.addColorStop(0, "#74b9ff");
                gradient.addColorStop(1, "#0984e3");
              } else if (backgroundType === "gradient12") {
                gradient.addColorStop(0, "#ff7675");
                gradient.addColorStop(1, "#74b9ff");
              } else if (backgroundType === "gradient13") {
                gradient.addColorStop(0, "#00c9ff");
                gradient.addColorStop(1, "#92fe9d");
              } else if (backgroundType === "gradient14") {
                gradient.addColorStop(0, "#0068f0");
                gradient.addColorStop(1, "#764ba2");
              } else if (backgroundType === "gradient15") {
                gradient.addColorStop(0, "#ff9a9e");
                gradient.addColorStop(1, "#fecfef");
              } else if (backgroundType === "gradient16") {
                gradient.addColorStop(0, "#2193b0");
                gradient.addColorStop(1, "#6dd5ed");
              } else if (backgroundType === "gradient17") {
                gradient.addColorStop(0, "#134e5e");
                gradient.addColorStop(1, "#71b280");
              } else if (backgroundType === "gradient18") {
                gradient.addColorStop(0, "#eb3349");
                gradient.addColorStop(1, "#f45c43");
              } else if (backgroundType === "gradient19") {
                gradient.addColorStop(0, "#4776e6");
                gradient.addColorStop(1, "#8e54e9");
              } else if (backgroundType === "gradient20") {
                gradient.addColorStop(0, "#f093fb");
                gradient.addColorStop(1, "#f5576c");
              } else if (backgroundType === "gradient21") {
                gradient.addColorStop(0, "#d299c2");
                gradient.addColorStop(1, "#fef9d7");
              } else if (backgroundType === "gradient22") {
                gradient.addColorStop(0, "#89f7fe");
                gradient.addColorStop(1, "#66a6ff");
              } else if (backgroundType === "gradient23") {
                gradient.addColorStop(0, "#ffe259");
                gradient.addColorStop(1, "#ffa751");
              } else if (backgroundType === "gradient24") {
                gradient.addColorStop(0, "#56ab2f");
                gradient.addColorStop(1, "#a8e6cf");
              }
              ctx.fillStyle = gradient;
            } else {
              ctx.fillStyle = bgOption.style;
            }
            ctx.fillRect(0, 0, canvas.width, canvas.height);
          }
        }

        // Draw image with rounded corners if specified
        if (borderRadius > 0) {
          ctx.save();
          ctx.beginPath();
          const x = padding.left;
          const y = padding.top;
          const width = img.width;
          const height = img.height;
          const radius = Math.min(borderRadius, width / 2, height / 2);

          ctx.moveTo(x + radius, y);
          ctx.lineTo(x + width - radius, y);
          ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
          ctx.lineTo(x + width, y + height - radius);
          ctx.quadraticCurveTo(
            x + width,
            y + height,
            x + width - radius,
            y + height
          );
          ctx.lineTo(x + radius, y + height);
          ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
          ctx.lineTo(x, y + radius);
          ctx.quadraticCurveTo(x, y, x + radius, y);
          ctx.closePath();
          ctx.clip();

          ctx.drawImage(img, padding.left, padding.top);
          ctx.restore();
        } else {
          ctx.drawImage(img, padding.left, padding.top);
        }

        // Draw text elements (NO selection indicators)
        textElements.forEach((textEl) => {
          const fontWeight = textEl.bold ? "bold" : "normal";
          const fontStyle = textEl.italic ? "italic" : "normal";
          ctx.font = `${fontStyle} ${fontWeight} ${textEl.fontSize}px ${textEl.fontFamily}`;
          ctx.fillStyle = textEl.color;
          ctx.textAlign = "left";
          ctx.textBaseline = "top";

          if (textEl.shadow) {
            ctx.shadowColor = "rgba(0, 0, 0, 0.5)";
            ctx.shadowBlur = 4;
            ctx.shadowOffsetX = 2;
            ctx.shadowOffsetY = 2;
          }

          ctx.fillText(textEl.text, textEl.x, textEl.y);

          // Draw underline if enabled
          if (textEl.underline) {
            const textMetrics = ctx.measureText(textEl.text);
            const underlineY = textEl.y + textEl.fontSize + 2;
            ctx.strokeStyle = textEl.color;
            ctx.lineWidth = Math.max(1, textEl.fontSize / 20);
            ctx.beginPath();
            ctx.moveTo(textEl.x, underlineY);
            ctx.lineTo(textEl.x + textMetrics.width, underlineY);
            ctx.stroke();
          }

          if (textEl.shadow) {
            ctx.shadowColor = "transparent";
            ctx.shadowBlur = 0;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 0;
          }
        });

        // Draw logo (NO selection indicators)
        if (logo) {
          const logoImg = new Image();
          logoImg.onload = () => {
            // Calculate logo size relative to IMAGE width (not canvas width)
            const imageWidth = canvas.width - padding.left - padding.right;
            const actualLogoSize = Math.round(
              imageWidth * (logoSizePercentage / 100)
            );

            // Calculate aspect ratio and maintain proportions
            const aspectRatio = logoImg.naturalWidth / logoImg.naturalHeight;
            let logoWidth, logoHeight;

            if (aspectRatio > 1) {
              // Wider than tall
              logoWidth = actualLogoSize;
              logoHeight = actualLogoSize / aspectRatio;
            } else {
              // Taller than wide or square
              logoHeight = actualLogoSize;
              logoWidth = actualLogoSize * aspectRatio;
            }

            // Position logo: percentage-based positioning with canvas center as reference
            let logoX, logoY;
            if (logoPosition.x <= 100 && logoPosition.y <= 100) {
              // Center the logo by default (x:50, y:50 = exact center)
              logoX = (canvas.width - logoWidth) / 2 + ((logoPosition.x - 50) * canvas.width / 100);
              logoY = (canvas.height - logoHeight) / 2 + ((logoPosition.y - 50) * canvas.height / 100);
            } else {
              // Legacy pixel positioning from right-bottom
              logoX = canvas.width - logoWidth - logoPosition.x;
              logoY = canvas.height - logoHeight - logoPosition.y;
            }
            ctx.drawImage(logoImg, logoX, logoY, logoWidth, logoHeight);

            // NO BOUNDING BOXES OR SELECTION INDICATORS - completely clean
            resolve();
          };
          logoImg.src = logo;
        } else {
          // Render BibeCheck logo when no custom logo is uploaded
          const logoSize = 160; // Fixed size for BibeCheck logo (4x bigger: 40 * 4)
          const margin = 20; // Distance from bottom-right corner

          // Position at lower right corner
          const logoX = canvas.width - logoSize - margin;
          const logoY = canvas.height - logoSize - margin;

          // Use our RotatedBookIcon component
          createBibeCheckLogoImage(logoSize).then((logoImg) => {
            ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
            resolve();
          });
        }
      };

      img.src = image;
    });
  };

  const handleDownload = () => {
    const canvas = getActiveCanvas();
    if (!canvas || isDownloading) return;

    // Start loading animation
    setIsDownloading(true);

    // Store current selection states to restore after download
    const currentLogoSelected = logoSelected;
    const currentSelectedTextId = selectedTextId;

    // Remove all selection states temporarily for clean download
    setLogoSelected(false);
    setSelectedTextId(null);

    const timestamp = Date.now();

    // Wait for UI to update
    setTimeout(() => {
      // Generate completely clean canvas
      generateCleanCanvasForDownload().then(() => {
        // Download the clean canvas
        const imageLink = document.createElement("a");
        imageLink.download = `bibecheck-screenshot-${timestamp}.png`;
        imageLink.href = canvas.toDataURL("image/png");
        imageLink.click();

        // Stop loading animation and restore selection states
        setIsDownloading(false);
        setLogoSelected(currentLogoSelected);
        setSelectedTextId(currentSelectedTextId);
      });
    }, 100);
  };

  const selectedText = textElements.find((el) => el.id === selectedTextId);

  // Helper function to calculate logo dimensions with aspect ratio
  const calculateLogoDimensions = (canvas) => {
    if (!logo) return null;

    const imageWidth = canvas.width - padding.left - padding.right;
    const actualLogoSize = Math.round(imageWidth * (logoSizePercentage / 100));

    // Create a temporary image to get aspect ratio
    const tempImg = new Image();
    tempImg.src = logo;
    const aspectRatio =
      tempImg.naturalWidth && tempImg.naturalHeight
        ? tempImg.naturalWidth / tempImg.naturalHeight
        : 1;

    // Calculate actual logo dimensions maintaining aspect ratio
    let logoWidth, logoHeight;
    if (aspectRatio > 1) {
      logoWidth = actualLogoSize;
      logoHeight = actualLogoSize / aspectRatio;
    } else {
      logoHeight = actualLogoSize;
      logoWidth = actualLogoSize * aspectRatio;
    }

    // Position logo: percentage-based positioning with canvas center as reference
    let logoX, logoY;
    if (logoPosition.x <= 100 && logoPosition.y <= 100) {
      // Center the logo by default (x:50, y:50 = exact center)
      logoX = (canvas.width - logoWidth) / 2 + ((logoPosition.x - 50) * canvas.width / 100);
      logoY = (canvas.height - logoHeight) / 2 + ((logoPosition.y - 50) * canvas.height / 100);
    } else {
      // Legacy pixel positioning from right-bottom
      logoX = canvas.width - logoWidth - logoPosition.x;
      logoY = canvas.height - logoHeight - logoPosition.y;
    }

    return { logoWidth, logoHeight, logoX, logoY, actualLogoSize, aspectRatio };
  };

  // Check if click is on logo (custom uploaded logo OR default BibeCheck logo)
  const isClickOnLogo = (clickX, clickY, canvas) => {
    // Check custom uploaded logo first
    if (logo) {
      const dimensions = calculateLogoDimensions(canvas);
      if (dimensions) {
        const { logoX, logoY, logoWidth, logoHeight } = dimensions;
        return (
          clickX >= logoX &&
          clickX <= logoX + logoWidth &&
          clickY >= logoY &&
          clickY <= logoY + logoHeight
        );
      }
    }

    // Check default BibeCheck logo (when no custom logo is uploaded)
    if (!logo) {
      const logoSize = 160; // Fixed size for BibeCheck logo (matches rendering)
      const margin = 20; // Distance from bottom-right corner
      const logoX = canvas.width - logoSize - margin;
      const logoY = canvas.height - logoSize - margin;

      return (
        clickX >= logoX &&
        clickX <= logoX + logoSize &&
        clickY >= logoY &&
        clickY <= logoY + logoSize
      );
    }

    return false;
  };

  // Check if click is on any logo resize handle and return which corner
  const getClickedLogoHandle = (clickX, clickY, canvas) => {
    return getMouseOverLogoHandle(clickX, clickY, canvas);
  };

  // Check which logo resize handle mouse is over (for cursor change)
  const getMouseOverLogoHandle = (mouseX, mouseY, canvas) => {
    if (!logoSelected) return null;

    const dimensions = calculateLogoDimensions(canvas);
    if (!dimensions) return null;

    const { logoX, logoY, logoWidth, logoHeight } = dimensions;

    const handleSize = 12;
    const handles = [
      { x: logoX, y: logoY, corner: "nw" }, // Top-left
      { x: logoX + logoWidth, y: logoY, corner: "ne" }, // Top-right
      { x: logoX, y: logoY + logoHeight, corner: "sw" }, // Bottom-left
      { x: logoX + logoWidth, y: logoY + logoHeight, corner: "se" }, // Bottom-right
    ];

    for (const handle of handles) {
      if (
        mouseX >= handle.x - handleSize / 2 &&
        mouseX <= handle.x + handleSize / 2 &&
        mouseY >= handle.y - handleSize / 2 &&
        mouseY <= handle.y + handleSize / 2
      ) {
        return handle.corner;
      }
    }
    return null;
  };

  // Handle canvas interactions for text and logo positioning
  const handleCanvasMouseDown = (e) => {
    const canvas = getActiveCanvas();
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const clickX = (e.clientX - rect.left) * scaleX;
    const clickY = (e.clientY - rect.top) * scaleY;

    // Check logo interactions first
    const clickedCorner = getClickedLogoHandle(
      clickX,
      clickY,
      canvas
    );
    if (clickedCorner) {
      const dimensions = calculateLogoDimensions(canvasRef.current);
      if (dimensions) {
        setIsLogoResizing(true);
        setResizeCorner(clickedCorner);
        setResizeStartState({
          mouseX: clickX,
          mouseY: clickY,
          logoX: dimensions.logoX,
          logoY: dimensions.logoY,
          logoWidth: dimensions.logoWidth,
          logoHeight: dimensions.logoHeight,
          logoSizePercent: logoSizePercentage,
          logoPos: { ...logoPosition },
          aspectRatio: dimensions.aspectRatio,
        });
      }
      return;
    }

    if (isClickOnLogo(clickX, clickY, canvasRef.current)) {
      // Double-click to replace logo, single click to select/drag
      if (e.detail === 2) { // Double-click
        logoInputRef.current?.click();
        return;
      }

      setLogoSelected(true);
      setIsLogoDragging(true);
      const dimensions = calculateLogoDimensions(canvasRef.current);
      if (dimensions) {
        setDragOffset({
          x: clickX - dimensions.logoX,
          y: clickY - dimensions.logoY,
        });
      }
      return;
    }

    // Deselect logo if clicking elsewhere
    setLogoSelected(false);

    // Handle text interactions
    if (selectedTextId) {
      const selectedTextElement = textElements.find(
        (el) => el.id === selectedTextId
      );
      if (selectedTextElement) {
        setDragOffset({
          x: clickX - selectedTextElement.x,
          y: clickY - selectedTextElement.y,
        });
        setIsDragging(true);
        setDraggedTextId(selectedTextId);
      }
    }
  };

  const handleCanvasMouseMove = (e) => {
    const canvas = getActiveCanvas();
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const mouseX = (e.clientX - rect.left) * scaleX;
    const mouseY = (e.clientY - rect.top) * scaleY;

    // Check for cursor change over handles
    if (!isLogoResizing && !isLogoDragging) {
      const overHandle = getMouseOverLogoHandle(
        mouseX,
        mouseY,
        canvas
      );
      setCursorOverHandle(overHandle);

      // Show pointer cursor when hovering over logo for replacement
      if (!overHandle && isClickOnLogo(mouseX, mouseY, canvasRef.current)) {
        canvas.style.cursor = 'pointer';
      }
    }

    // Handle logo resizing - only the dragged corner moves, opposite corner stays fixed
    if (isLogoResizing && resizeCorner && resizeStartState) {
      const canvas = canvasRef.current;
      const imageWidth = canvas.width - padding.left - padding.right;

      // Calculate distance from the fixed corner to mouse position
      let fixedCornerX, fixedCornerY;

      // Determine the fixed corner position based on which corner is being dragged
      switch (resizeCorner) {
        case "se": // Dragging bottom-right, top-left is fixed
          fixedCornerX = resizeStartState.logoX;
          fixedCornerY = resizeStartState.logoY;
          break;
        case "nw": // Dragging top-left, bottom-right is fixed
          fixedCornerX = resizeStartState.logoX + resizeStartState.logoWidth;
          fixedCornerY = resizeStartState.logoY + resizeStartState.logoHeight;
          break;
        case "ne": // Dragging top-right, bottom-left is fixed
          fixedCornerX = resizeStartState.logoX;
          fixedCornerY = resizeStartState.logoY + resizeStartState.logoHeight;
          break;
        case "sw": // Dragging bottom-left, top-right is fixed
          fixedCornerX = resizeStartState.logoX + resizeStartState.logoWidth;
          fixedCornerY = resizeStartState.logoY;
          break;
      }

      // Calculate new dimensions based on distance from fixed corner to mouse
      const newWidth = Math.abs(mouseX - fixedCornerX);
      const newHeight = Math.abs(mouseY - fixedCornerY);

      // Maintain aspect ratio by using the larger dimension
      const aspectRatio = resizeStartState.aspectRatio;
      let finalWidth, finalHeight;

      if (newWidth / aspectRatio > newHeight) {
        finalWidth = newWidth;
        finalHeight = newWidth / aspectRatio;
      } else {
        finalHeight = newHeight;
        finalWidth = newHeight * aspectRatio;
      }

      // Calculate new size percentage based on the larger dimension
      const newSizePixels = Math.max(finalWidth, finalHeight);
      const newSizePercent = Math.max(
        5,
        Math.min(100, (newSizePixels / imageWidth) * 100)
      );

      // Calculate actual logo dimensions with new size
      const actualNewSize = Math.round(imageWidth * (newSizePercent / 100));
      let actualWidth, actualHeight;

      if (aspectRatio > 1) {
        actualWidth = actualNewSize;
        actualHeight = actualNewSize / aspectRatio;
      } else {
        actualHeight = actualNewSize;
        actualWidth = actualNewSize * aspectRatio;
      }

      // Calculate new position to maintain the fixed corner
      let newLogoX, newLogoY;

      switch (resizeCorner) {
        case "se": // Top-left is fixed
          newLogoX = fixedCornerX;
          newLogoY = fixedCornerY;
          break;
        case "nw": // Bottom-right is fixed
          newLogoX = fixedCornerX - actualWidth;
          newLogoY = fixedCornerY - actualHeight;
          break;
        case "ne": // Bottom-left is fixed
          newLogoX = fixedCornerX;
          newLogoY = fixedCornerY - actualHeight;
          break;
        case "sw": // Top-right is fixed
          newLogoX = fixedCornerX - actualWidth;
          newLogoY = fixedCornerY;
          break;
      }

      // Convert to center-based percentage positioning
      const centerX = (canvas.width - actualWidth) / 2;
      const centerY = (canvas.height - actualHeight) / 2;

      // Calculate offset from center as percentage
      const offsetX = ((newLogoX - centerX) / canvas.width) * 100;
      const offsetY = ((newLogoY - centerY) / canvas.height) * 100;

      // Convert to logoPosition format (50,50 = center)
      const posX = 50 + offsetX;
      const posY = 50 + offsetY;

      // Constrain within reasonable bounds
      const constrainedPosX = Math.max(-50, Math.min(150, posX));
      const constrainedPosY = Math.max(-50, Math.min(150, posY));

      setLogoSizePercentage(Math.round(newSizePercent));
      setLogoPosition({ x: constrainedPosX, y: constrainedPosY });
      return;
    }

    // Handle logo dragging
    if (isLogoDragging && logo) {
      const dimensions = calculateLogoDimensions(canvasRef.current);
      if (dimensions) {
        let newX = mouseX - dragOffset.x;
        let newY = mouseY - dragOffset.y;

        // Convert position to percentage-based system with center as reference
        const centerX = (canvasRef.current.width - dimensions.logoWidth) / 2;
        const centerY = (canvasRef.current.height - dimensions.logoHeight) / 2;

        // Calculate offset from center as percentage
        const offsetX = ((newX - centerX) / canvasRef.current.width) * 100;
        const offsetY = ((newY - centerY) / canvasRef.current.height) * 100;

        // Convert to logoPosition format (50,50 = center)
        const posX = 50 + offsetX;
        const posY = 50 + offsetY;

        // Constrain within reasonable bounds (allow some positioning outside for flexibility)
        const constrainedPosX = Math.max(-50, Math.min(150, posX));
        const constrainedPosY = Math.max(-50, Math.min(150, posY));

        setLogoPosition({ x: constrainedPosX, y: constrainedPosY });
      }
      return;
    }

    // Handle text dragging with bounds checking
    if (isDragging && draggedTextId) {
      const canvas = canvasRef.current;
      const textElement = textElements.find((el) => el.id === draggedTextId);

      if (textElement && canvas) {
        // Create a temporary canvas context to measure text dimensions
        const tempCanvas = document.createElement("canvas");
        const tempCtx = tempCanvas.getContext("2d");
        const fontWeight = textElement.bold ? "bold" : "normal";
        const fontStyle = textElement.italic ? "italic" : "normal";
        tempCtx.font = `${fontStyle} ${fontWeight} ${textElement.fontSize}px ${textElement.fontFamily}`;

        const textMetrics = tempCtx.measureText(textElement.text);
        const textWidth = textMetrics.width;
        const textHeight = textElement.fontSize; // Approximate height

        // Calculate new position with drag offset
        let newX = mouseX - dragOffset.x;
        let newY = mouseY - dragOffset.y;

        // Apply bounds constraints
        newX = Math.max(0, Math.min(canvas.width - textWidth, newX));
        newY = Math.max(0, Math.min(canvas.height - textHeight, newY));

        updateTextElement(draggedTextId, { x: newX, y: newY });
      }
    }
  };

  const handleCanvasMouseUp = () => {
    setIsDragging(false);
    setDraggedTextId(null);
    setIsLogoDragging(false);
    setIsLogoResizing(false);
    setResizeCorner(null);
    setCursorOverHandle(null);
    setResizeStartState(null);
    setDragOffset({ x: 0, y: 0 });

    // Force regenerate canvas after any interaction to ensure consistency
    setTimeout(generatePreview, 50);
  };

  const handleCanvasClick = () => {
    // Remove click positioning - text should only move via dragging
    // This prevents the jumping behavior
  };

  return createPortal(
    <div className="fixed inset-0 z-[99998]" style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)' }}>
      <div className="h-full flex items-center justify-center p-2">
        <div className="bg-white rounded-2xl max-w-6xl w-full h-full max-h-[95vh] flex flex-col lg:flex-row overflow-hidden">
        {/* Mobile Layout - Stack image first, then controls */}
        <div className="lg:hidden flex flex-col h-full">
          {/* Header */}
          <div className="p-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800">Enhance</h2>
              <button
                onClick={onClose}
                className="p-1 rounded-lg hover:bg-gray-200 text-gray-500"
                title="Close enhancement editor">
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Image Area - Takes up 60% of the screen */}
          <div className="bg-gray-100 p-3 flex items-center justify-center" style={{ height: '60vh' }}>
            <canvas
              ref={mobileCanvasRef}
              onClick={handleCanvasClick}
              onMouseDown={handleCanvasMouseDown}
              onMouseMove={handleCanvasMouseMove}
              onMouseUp={handleCanvasMouseUp}
              onMouseLeave={handleCanvasMouseUp}
              className="border rounded-lg shadow-lg bg-white max-w-full max-h-full"
              style={{
                cursor:
                  isDragging || isLogoDragging
                    ? "grabbing"
                    : isLogoResizing
                    ? `${resizeCorner}-resize`
                    : cursorOverHandle
                    ? `${cursorOverHandle}-resize`
                    : "crosshair",
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
            />
          </div>

          {/* Scrollable Controls Area - Fixed height at bottom */}
          <div className="bg-white border-t border-gray-200 overflow-y-auto" style={{ height: '30vh' }}>
            {/* Text Controls */}
            <div className="border-b border-gray-200 p-3">
              <div className="flex flex-col gap-3">
                {/* Text Input */}
                <div className="w-full">
                  <input
                    type="text"
                    value={
                      selectedText?.text ||
                      (textElements.length === 0
                        ? savedTextSettings?.lastTextValue || ""
                        : "")
                    }
                    onChange={(e) => {
                      const newText = e.target.value;
                      if (selectedText) {
                        updateTextElement(selectedText.id, { text: newText });
                      } else if (textElements.length === 0) {
                        setSavedTextSettings((prev) => ({
                          ...prev,
                          lastTextValue: newText,
                        }));
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !selectedText) {
                        const trimmedText = e.target.value.trim();
                        if (trimmedText) {
                          addTextElement(trimmedText);
                          setSavedTextSettings((prev) => ({
                            ...prev,
                            lastTextValue: "",
                          }));
                        }
                      }
                    }}
                    placeholder={
                      selectedText
                        ? "Edit text..."
                        : textElements.length === 0
                        ? "Add text to your image..."
                        : "Select text to edit or add new text..."
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                  />
                </div>

                {/* Font Controls - Simplified for mobile */}
                <div className="flex flex-wrap items-center gap-2">
                  {/* Size Control */}
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <label className="text-xs text-gray-600">Size:</label>
                    <input
                      type="range"
                      min="12"
                      max="300"
                      value={selectedText?.fontSize || 64}
                      onChange={(e) => {
                        const newSize = parseInt(e.target.value);
                        if (selectedText) {
                          updateTextElement(selectedText.id, { fontSize: newSize });
                        }
                      }}
                      className="flex-1"
                    />
                    <span className="text-xs text-gray-500 min-w-8">
                      {selectedText?.fontSize || 64}
                    </span>
                  </div>

                  {/* Color and Font Row */}
                  <div className="flex items-center gap-2 w-full">
                    <input
                      type="color"
                      value={selectedText?.color || "#ffffff"}
                      onChange={(e) => {
                        const newColor = e.target.value;
                        if (selectedText) {
                          updateTextElement(selectedText.id, { color: newColor });
                        }
                      }}
                      className="w-8 h-8 rounded border"
                    />

                    <select
                      value={selectedText?.fontFamily || "Inter, sans-serif"}
                      onChange={(e) => {
                        const newFont = e.target.value;
                        if (selectedText) {
                          updateTextElement(selectedText.id, { fontFamily: newFont });
                        }
                      }}
                      className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded bg-white">
                      {fontOptions.map((font) => (
                        <option key={font.id} value={font.family}>
                          {font.name}
                        </option>
                      ))}
                    </select>

                    {/* Style Buttons */}
                    <button
                      onClick={() => {
                        if (selectedText) {
                          updateTextElement(selectedText.id, {
                            bold: !selectedText.bold,
                          });
                        }
                      }}
                      className={`p-1 rounded text-xs border ${
                        selectedText?.bold
                          ? "bg-blue-500 text-white border-blue-500"
                          : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      }`}>
                      <Bold className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => {
                        if (selectedText) {
                          updateTextElement(selectedText.id, {
                            italic: !selectedText.italic,
                          });
                        }
                      }}
                      className={`p-1 rounded text-xs border ${
                        selectedText?.italic
                          ? "bg-blue-500 text-white border-blue-500"
                          : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      }`}>
                      <Italic className="w-3 h-3" />
                    </button>

                    {/* Download Button */}
                    <button
                      onClick={handleDownload}
                      disabled={isDownloading}
                      className={`${
                        isDownloading
                          ? "bg-blue-400 cursor-not-allowed"
                          : "bg-blue-600 hover:bg-blue-700"
                      } text-white p-2 rounded-lg transition-colors shadow-lg`}
                      title={
                        isDownloading
                          ? "Preparing download..."
                          : capturedStyles
                          ? "Download enhanced screenshot + style data (JSON)"
                          : "Download enhanced screenshot"
                      }>
                      {isDownloading ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Background Controls */}
            <div className="p-3 border-b border-gray-200">
              <h3 className="font-semibold text-gray-800 mb-2 text-sm">Background</h3>
              <div className="grid grid-cols-6 gap-2">
                {backgroundOptions.map((bg) => (
                  <button
                    key={bg.id}
                    onClick={() => {
                      if (bg.id === "custom") {
                        setBackgroundType("custom");
                      } else {
                        setBackgroundType(bg.id);
                        setCustomColor(bg.style);
                      }
                    }}
                    className={`aspect-square rounded-md border-2 transition-all hover:scale-105 ${
                      backgroundType === bg.id ||
                      (bg.id === "custom" && backgroundType === "custom")
                        ? "border-blue-500 shadow-md"
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                    style={{
                      background: bg.id === "custom" ? customColor : bg.style,
                    }}
                    title={bg.label}>
                    {bg.id === "custom" ? (
                      <div className="w-full h-full rounded-md flex items-center justify-center text-gray-600 hover:text-gray-800">
                        <Palette className="w-4 h-4" />
                      </div>
                    ) : (
                      <div
                        className="w-full h-full rounded-md"
                        style={{ background: bg.style }}
                      />
                    )}
                  </button>
                ))}
              </div>

              {/* Custom Color Input */}
              {backgroundType === "custom" && (
                <div className="mt-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Custom Background Color
                  </label>
                  <div className="flex gap-2 items-center">
                    <input
                      type="color"
                      value={customColor}
                      onChange={(e) => setCustomColor(e.target.value)}
                      className="w-12 h-8 rounded border border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={customColor}
                      onChange={(e) => setCustomColor(e.target.value)}
                      className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                      placeholder="#ff6b6b"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Padding & Corners Controls */}
            <div className="p-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-gray-800 text-sm">Padding & Corners</h3>
                <button
                  onClick={handlePaddingToggle}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                  title={
                    padding.independentPadding
                      ? "Switch to uniform padding"
                      : "Switch to independent padding"
                  }>
                  {padding.independentPadding ? "Independent" : "Uniform"} Padding
                </button>
              </div>

              {!padding.independentPadding ? (
                // Uniform Padding
                <div className="space-y-3">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-medium text-gray-700">Padding</label>
                      <span className="text-xs text-gray-500">
                        {Math.round((padding.top / 300) * 100)}%
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="300"
                      value={padding.top}
                      onChange={(e) => {
                        const newValue = parseInt(e.target.value);
                        setPadding({
                          top: newValue,
                          right: newValue,
                          bottom: newValue,
                          left: newValue,
                          independentPadding: false,
                          horizontalLocked: true,
                          verticalLocked: true,
                        });
                      }}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-medium text-gray-700">Corner Radius</label>
                      <span className="text-xs text-gray-500">{borderRadius}px</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="50"
                      value={borderRadius}
                      onChange={(e) => setBorderRadius(parseInt(e.target.value))}
                      className="w-full"
                    />
                  </div>
                </div>
              ) : (
                // Independent Padding Controls - Simplified for mobile
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-700 block mb-1">
                        Top: {Math.round((padding.top / 300) * 100)}%
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="300"
                        value={padding.top}
                        onChange={(e) => {
                          const newTop = parseInt(e.target.value);
                          setPadding((prev) => ({
                            ...prev,
                            top: newTop,
                            bottom: prev.verticalLocked ? newTop : prev.bottom,
                          }));
                        }}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-700 block mb-1">
                        Bottom: {Math.round((padding.bottom / 300) * 100)}%
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="300"
                        value={padding.bottom}
                        onChange={(e) => {
                          const newBottom = parseInt(e.target.value);
                          setPadding((prev) => ({
                            ...prev,
                            bottom: newBottom,
                            top: prev.verticalLocked ? newBottom : prev.top,
                          }));
                        }}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-700 block mb-1">
                        Left: {Math.round((padding.left / 300) * 100)}%
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="300"
                        value={padding.left}
                        onChange={(e) => {
                          const newLeft = parseInt(e.target.value);
                          setPadding((prev) => ({
                            ...prev,
                            left: newLeft,
                            right: prev.horizontalLocked ? newLeft : prev.right,
                          }));
                        }}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-700 block mb-1">
                        Right: {Math.round((padding.right / 300) * 100)}%
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="300"
                        value={padding.right}
                        onChange={(e) => {
                          const newRight = parseInt(e.target.value);
                          setPadding((prev) => ({
                            ...prev,
                            right: newRight,
                            left: prev.horizontalLocked ? newRight : prev.left,
                          }));
                        }}
                        className="w-full"
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-medium text-gray-700">Corner Radius</label>
                      <span className="text-xs text-gray-500">{borderRadius}px</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="50"
                      value={borderRadius}
                      onChange={(e) => setBorderRadius(parseInt(e.target.value))}
                      className="w-full"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Desktop Layout - Original Layout */}
        <div className="hidden lg:flex flex-1 flex-col bg-gray-100 min-h-0 overflow-y-auto">
          {/* Text Controls at Top - Always Show */}
          <div className="bg-white border-b border-gray-200 p-3 flex-shrink-0 order-2 lg:order-1">
            <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
              {/* Text Input - Larger and takes more space */}
              <div className="flex-1 min-w-0">
                <input
                  type="text"
                  value={
                    selectedText?.text ||
                    (textElements.length === 0
                      ? savedTextSettings?.lastTextValue || ""
                      : "")
                  }
                  onChange={(e) => {
                    if (selectedText) {
                      updateTextElement(selectedText.id, {
                        text: e.target.value,
                      });
                    } else if (textElements.length === 0) {
                      // Create new text element with the typed text
                      addTextElement(e.target.value);
                    }
                  }}
                  onFocus={() => {
                    if (!selectedText && textElements.length === 0) {
                      addTextElement();
                    }
                  }}
                  className="w-full p-3 text-base border border-gray-300 rounded-lg"
                  placeholder="Type your text here..."
                  title="Click to add or edit text"
                />
              </div>

              {/* Font Controls - Moved to rightmost */}
              <div className="flex flex-wrap lg:flex-nowrap items-center gap-2 sm:gap-3 flex-shrink-0">
                {/* Size Control */}
                <div className="flex items-center gap-2 min-w-0">
                  <label className="text-xs text-gray-600 whitespace-nowrap">Size:</label>
                  <input
                    type="range"
                    min="12"
                    max="300"
                    value={selectedText?.fontSize || 100}
                    onChange={(e) => {
                      if (selectedText) {
                        updateTextElement(selectedText.id, {
                          fontSize: parseInt(e.target.value),
                        });
                      }
                    }}
                    className="w-16 sm:w-20"
                  />
                  <span className="text-xs text-gray-600 w-8 sm:w-12 text-right">
                    {selectedText?.fontSize || 100}px
                  </span>
                </div>

                {/* Color Picker */}
                <input
                  type="color"
                  value={selectedText?.color || "#000000"}
                  onChange={(e) => {
                    if (selectedText) {
                      updateTextElement(selectedText.id, {
                        color: e.target.value,
                      });
                    }
                  }}
                  className="w-8 h-8 rounded border flex-shrink-0"
                />

                {/* Font Family */}
                <select
                  value={selectedText?.fontFamily || "Inter, sans-serif"}
                  onChange={(e) => {
                    if (selectedText) {
                      updateTextElement(selectedText.id, {
                        fontFamily: e.target.value,
                      });
                    }
                  }}
                  className="p-1 text-xs border border-gray-300 rounded min-w-0 max-w-[120px] lg:max-w-none">
                  {fontOptions.map((font) => (
                    <option key={font.id} value={font.family}>
                      {font.name}
                    </option>
                  ))}
                </select>

                {/* Style Buttons */}
                <div className="flex gap-1 flex-shrink-0">
                  <button
                    onClick={() => {
                      if (selectedText) {
                        updateTextElement(selectedText.id, {
                          bold: !selectedText.bold,
                        });
                      }
                    }}
                    className={`p-1 rounded ${
                      selectedText?.bold
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                    title="Toggle bold text">
                    <Bold className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      if (selectedText) {
                        updateTextElement(selectedText.id, {
                          italic: !selectedText.italic,
                        });
                      }
                    }}
                    className={`p-1 rounded ${
                      selectedText?.italic
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                    title="Toggle italic text">
                    <Italic className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      if (selectedText) {
                        updateTextElement(selectedText.id, {
                          underline: !selectedText.underline,
                        });
                      }
                    }}
                    className={`p-1 rounded ${
                      selectedText?.underline
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                    title="Toggle underline text">
                    <Underline className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      if (selectedText) {
                        updateTextElement(selectedText.id, {
                          shadow: !selectedText.shadow,
                        });
                      }
                    }}
                    className={`p-1 rounded ${
                      selectedText?.shadow
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                    title="Toggle text shadow">
                    <Zap className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Compact Download Button - Icon Only */}
              <div className="flex-shrink-0">
                <button
                  onClick={handleDownload}
                  disabled={isDownloading}
                  className={`${
                    isDownloading
                      ? "bg-blue-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700"
                  } text-white p-2 rounded-lg transition-colors shadow-lg`}
                  title={
                    isDownloading
                      ? "Preparing download..."
                      : capturedStyles
                      ? "Download enhanced screenshot + style data (JSON)"
                      : "Download enhanced screenshot"
                  }>
                  {isDownloading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                </button>
              </div>

              {/* Hidden file input for logo upload */}
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
              />
            </div>
          </div>

          {/* Canvas Area - Smart Responsive Sizing */}
          <div className="flex-1 flex flex-col p-4 relative min-h-0 overflow-y-auto order-1 lg:order-2">
            <div className="flex-1 flex items-center justify-center min-h-0">
              <canvas
                ref={canvasRef}
                onClick={handleCanvasClick}
                onMouseDown={handleCanvasMouseDown}
                onMouseMove={handleCanvasMouseMove}
                onMouseUp={handleCanvasMouseUp}
                onMouseLeave={handleCanvasMouseUp}
                className="border rounded-lg shadow-lg bg-white"
                style={{
                  cursor:
                    isDragging || isLogoDragging
                      ? "grabbing"
                      : isLogoResizing
                      ? `${resizeCorner}-resize`
                      : cursorOverHandle
                      ? `${cursorOverHandle}-resize`
                      : "crosshair",
                  // Smart responsive sizing: Mobile vs Desktop
                  maxWidth: 'min(100%, calc(100vw - 32px))', // Mobile: account for padding
                  maxHeight: 'min(100%, calc(100vh - 300px))', // Mobile: account for header + text controls + some space for background controls
                  // Desktop sizing handled by container constraints
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  // Ensure minimum size for usability
                  minWidth: '300px',
                  minHeight: '200px'
                }}
              />
            </div>

          </div>
        </div>

        {/* Right Panel - All Controls */}
        <div className="w-full lg:w-72 bg-gray-50 border-t lg:border-t-0 lg:border-l border-gray-200 flex flex-col order-last flex-shrink-0">
          {/* Desktop Header */}
          <div className="hidden lg:block p-3 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800">Controls</h2>
              <button
                onClick={onClose}
                className="p-1 rounded-lg hover:bg-gray-200 text-gray-500"
                title="Close enhancement editor">
                <X size={18} />
              </button>
            </div>
          </div>

          {/* All Controls */}
          <div className="flex-1 overflow-y-auto p-3 space-y-4 lg:min-h-0">
            {/* Background Section */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-2 text-sm">
                Background
              </h3>
              <div className="grid grid-cols-6 gap-1">
                {backgroundOptions.map((bg) => (
                  <button
                    key={bg.id}
                    onClick={() => {
                      setBackgroundType(bg.id);
                      if (bg.id === "custom") {
                        // Custom color picker will be handled below
                      }
                    }}
                    className={`aspect-square rounded-lg border-2 transition-all relative group ${
                      backgroundType === bg.id
                        ? "border-blue-500 ring-2 ring-blue-200"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    title={bg.label}>
                    {bg.id === "custom" ? (
                      <div className="w-full h-full rounded-md flex items-center justify-center text-gray-600 hover:text-gray-800">
                        <Palette className="w-4 h-4" />
                      </div>
                    ) : (
                      <div
                        className="w-full h-full rounded-md"
                        style={{
                          background:
                            bg.style === "transparent"
                              ? "repeating-conic-gradient(#808080 0% 25%, transparent 0% 50%) 50% / 8px 8px"
                              : bg.style,
                        }}
                      />
                    )}
                  </button>
                ))}
              </div>

              {/* Custom Color Picker */}
              {backgroundType === "custom" && (
                <div className="mt-3 p-3 border rounded-lg bg-gray-50">
                  <label className="block text-xs text-gray-600 mb-2">
                    Custom Background Color
                  </label>
                  <div className="flex gap-2 items-center">
                    <input
                      type="color"
                      value={customColor}
                      onChange={(e) => setCustomColor(e.target.value)}
                      className="w-12 h-8 rounded border border-gray-300 cursor-pointer"
                    />
                    <span className="text-xs text-gray-500 font-mono">
                      {customColor}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Padding & Corners Section */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-gray-800 text-sm">
                  Padding & Corners
                </h3>
                <button
                  onClick={handlePaddingToggle}
                  className="p-1 rounded hover:bg-gray-200 transition-colors"
                  title={
                    padding.uniform
                      ? "Switch to individual controls"
                      : "Switch to single control"
                  }>
                  {padding.uniform ? (
                    <Settings2 size={14} />
                  ) : (
                    <GitCommitHorizontal size={14} />
                  )}
                </button>
              </div>

              {/* Padding Controls */}
              <div className="mb-3">
                {padding.uniform ? (
                  // Uniform Padding Mode
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Padding: {Math.round((padding.top / 300) * 100)}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="300"
                      value={padding.top}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        setPadding({
                          top: value,
                          right: value,
                          bottom: value,
                          left: value,
                          uniform: true,
                        });
                      }}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none slider"
                    />
                  </div>
                ) : (
                  // Independent Padding Mode - Cross Layout (Fixed Spacing)
                  <div className="space-y-2">
                    <div className="text-xs text-gray-600 text-center mb-3">
                      Independent Padding
                    </div>

                    {/* Top Section */}
                    <div className="flex justify-center mb-2">
                      <div className="flex flex-col items-center">
                        <div className="text-xs text-gray-500 mb-1 font-medium">
                          {Math.round((padding.top / 300) * 100)}%
                        </div>
                        <div className="w-16 mb-1">
                          <input
                            type="range"
                            min="0"
                            max="300"
                            value={padding.top}
                            onChange={(e) => {
                              const value = parseInt(e.target.value);
                              setPadding((prev) =>
                                padding.verticalLocked
                                  ? {
                                      ...prev,
                                      top: value,
                                      bottom: value,
                                    }
                                  : {
                                      ...prev,
                                      top: value,
                                    }
                              );
                            }}
                            className="w-full h-1 bg-gray-300 rounded-lg appearance-none slider"
                          />
                        </div>
                        <div className="text-xs text-gray-400 font-medium">
                          Top
                        </div>
                      </div>
                    </div>

                    {/* Vertical Chain Link - Between Top and Bottom */}
                    <div className="flex justify-center mb-2">
                      <button
                        onClick={handleVerticalLockToggle}
                        className="p-1 rounded hover:bg-gray-100 transition-colors group"
                        title={
                          padding.verticalLocked
                            ? "Unlink top and bottom"
                            : "Link top and bottom together"
                        }>
                        <div className="flex flex-col items-center gap-1">
                          <div
                            className={`w-0.5 h-3 ${
                              padding.verticalLocked
                                ? "bg-blue-500"
                                : "bg-gray-300"
                            } transition-colors`}></div>
                          {padding.verticalLocked ? (
                            <Link
                              size={12}
                              className="text-blue-500 rotate-90"
                            />
                          ) : (
                            <Unlink
                              size={12}
                              className="text-gray-400 rotate-90"
                            />
                          )}
                          <div
                            className={`w-0.5 h-3 ${
                              padding.verticalLocked
                                ? "bg-blue-500"
                                : "bg-gray-300"
                            } transition-colors`}></div>
                        </div>
                      </button>
                    </div>

                    {/* Middle Section - Left + Horizontal Chain + Right */}
                    <div className="flex items-center justify-center gap-2">
                      {/* Left Control */}
                      <div className="flex flex-col items-center">
                        <div className="text-xs text-gray-500 mb-1 font-medium">
                          {Math.round((padding.left / 300) * 100)}%
                        </div>
                        <div className="w-16 mb-1">
                          <input
                            type="range"
                            min="0"
                            max="300"
                            value={padding.left}
                            onChange={(e) => {
                              const value = parseInt(e.target.value);
                              setPadding((prev) =>
                                padding.horizontalLocked
                                  ? {
                                      ...prev,
                                      left: value,
                                      right: value,
                                    }
                                  : {
                                      ...prev,
                                      left: value,
                                    }
                              );
                            }}
                            className="w-full h-1 bg-gray-300 rounded-lg appearance-none slider"
                          />
                        </div>
                        <div className="text-xs text-gray-400 font-medium">
                          Left
                        </div>
                      </div>

                      {/* Horizontal Chain Link - Between Left and Right */}
                      <div className="flex items-center">
                        <button
                          onClick={handleHorizontalLockToggle}
                          className="p-2 rounded hover:bg-gray-100 transition-colors group"
                          title={
                            padding.horizontalLocked
                              ? "Unlink left and right"
                              : "Link left and right together"
                          }>
                          <div className="flex items-center gap-1">
                            <div
                              className={`w-4 h-0.5 ${
                                padding.horizontalLocked
                                  ? "bg-blue-500"
                                  : "bg-gray-300"
                              } transition-colors`}></div>
                            {padding.horizontalLocked ? (
                              <Link size={14} className="text-blue-500" />
                            ) : (
                              <Unlink size={14} className="text-gray-400" />
                            )}
                            <div
                              className={`w-4 h-0.5 ${
                                padding.horizontalLocked
                                  ? "bg-blue-500"
                                  : "bg-gray-300"
                              } transition-colors`}></div>
                          </div>
                        </button>
                      </div>

                      {/* Right Control */}
                      <div className="flex flex-col items-center">
                        <div className="text-xs text-gray-500 mb-1 font-medium">
                          {Math.round((padding.right / 300) * 100)}%
                        </div>
                        <div className="w-16 mb-1">
                          <input
                            type="range"
                            min="0"
                            max="300"
                            value={padding.right}
                            onChange={(e) => {
                              const value = parseInt(e.target.value);
                              setPadding((prev) =>
                                padding.horizontalLocked
                                  ? {
                                      ...prev,
                                      left: value,
                                      right: value,
                                    }
                                  : {
                                      ...prev,
                                      right: value,
                                    }
                              );
                            }}
                            className="w-full h-1 bg-gray-300 rounded-lg appearance-none slider"
                          />
                        </div>
                        <div className="text-xs text-gray-400 font-medium">
                          Right
                        </div>
                      </div>
                    </div>

                    {/* Another Vertical Chain Link - Between Bottom and Top */}
                    <div className="flex justify-center mb-2">
                      <button
                        onClick={handleVerticalLockToggle}
                        className="p-1 rounded hover:bg-gray-100 transition-colors group"
                        title={
                          padding.verticalLocked
                            ? "Unlink top and bottom"
                            : "Link top and bottom together"
                        }>
                        <div className="flex flex-col items-center gap-1">
                          <div
                            className={`w-0.5 h-3 ${
                              padding.verticalLocked
                                ? "bg-blue-500"
                                : "bg-gray-300"
                            } transition-colors`}></div>
                          {padding.verticalLocked ? (
                            <Link
                              size={12}
                              className="text-blue-500 rotate-90"
                            />
                          ) : (
                            <Unlink
                              size={12}
                              className="text-gray-400 rotate-90"
                            />
                          )}
                          <div
                            className={`w-0.5 h-3 ${
                              padding.verticalLocked
                                ? "bg-blue-500"
                                : "bg-gray-300"
                            } transition-colors`}></div>
                        </div>
                      </button>
                    </div>

                    {/* Bottom Section */}
                    <div className="flex justify-center">
                      <div className="flex flex-col items-center">
                        <div className="text-xs text-gray-500 mb-1 font-medium">
                          {Math.round((padding.bottom / 300) * 100)}%
                        </div>
                        <div className="w-16 mb-1">
                          <input
                            type="range"
                            min="0"
                            max="300"
                            value={padding.bottom}
                            onChange={(e) => {
                              const value = parseInt(e.target.value);
                              setPadding((prev) =>
                                padding.verticalLocked
                                  ? {
                                      ...prev,
                                      top: value,
                                      bottom: value,
                                    }
                                  : {
                                      ...prev,
                                      bottom: value,
                                    }
                              );
                            }}
                            className="w-full h-1 bg-gray-300 rounded-lg appearance-none slider"
                          />
                        </div>
                        <div className="text-xs text-gray-400 font-medium">
                          Bottom
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Corners */}
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Corners: {Math.round((borderRadius / 100) * 100)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={borderRadius}
                  onChange={(e) => setBorderRadius(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none slider"
                />
              </div>
            </div>

          </div>
        </div>
        </div>

        {/* Custom Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 backdrop-blur-sm flex items-center justify-center z-[99999]" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
            <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm mx-4 border border-gray-200">
              {/* Icon and Title */}
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-gradient-to-r from-red-100 to-red-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🗑️</span>
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">Remove Logo</h3>
                <p className="text-gray-600 text-sm">
                  Remove this logo and restore the default BibeCheck logo?
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors font-medium text-gray-700 text-center"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setLogo(null);
                    setLogoSelected(false);
                    setLogoPosition({ x: 50, y: 50 });
                    if (logoInputRef.current) {
                      logoInputRef.current.value = '';
                    }
                    setShowDeleteConfirm(false);
                  }}
                  className="flex-1 px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium text-center"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default EnhancementEditor;
